<template>
  <div class="billing_index">
    <page-header :title="$tc('billing.name', 1)">
    </page-header>
    <page-content>
        <div v-if="loading" class="p-3">
          <i class="fa fa-spinner fa-spin mr-2"></i> Loading billing profiles...
        </div>
        <div v-else>
          <div v-if="error" class="alert alert-danger">
            {{ error }}
          </div>
          <div v-else>
            <h3>{{$tc('billingProfile.name', 2)}}</h3>
            <div v-if="profiles.length === 0" class="p-3 text-muted">
              No billing profiles found.
            </div>
            <div v-else>
              <div class="row">
                <div v-for="(profile, idx) in profiles" :key="profile.id || idx" class="col-md-6 col-lg-4">
                  <router-link class="block block-rounded block-link-pop pb-3" :to="{ name: 'billing.show', params: { clientId: $store.state.currentClient.uuid, id: profile.id } }">
                    <div class="block-content">
                      <div class="d-flex align-items-center mb-2">
                        <div class="flex-grow-1">
                          <div class="font-w600 mb-1">{{ profile.name || profile.companyName || 'Billing Profile' }}</div>
                          <div class="text-muted small">ID: {{ profile.id }}</div>
                        </div>
                        <div class="ml-2 text-right">
                          <div class="badge badge-pill badge-secondary mb-1">{{ profile.type === 'INDIVIDUAL' ? 'Individual' : 'Organization' }}</div>
                          <div class="badge badge-pill badge-primary">{{ profile.currency || '—' }}</div>
                        </div>
                      </div>
                      <div class="text-muted small">
                        <div v-if="profile.email"><i class="fa fa-envelope mr-1"></i>{{ profile.email }}</div>
                        <div v-if="profile.phone"><i class="fa fa-phone mr-1"></i>{{ profile.phone }}</div>
                        <div v-if="hasAddress(profile)"><i class="fa fa-map-marker mr-1"></i>{{ formatAddress(profile) }}</div>
                        <div v-if="cardPaymentsEnabled && (profile.pmType || profile.pmLastFour)"><i class="fa fa-credit-card mr-1"></i>{{ formatPayment(profile) }}</div>
                      </div>
                    </div>
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Invoices section below billing profiles -->
            <div class="mt-4 p-3 rounded bg-white border">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h5 class="mb-0">Invoices</h5>
                <div class="small text-muted">Page {{ invoicesMeta.current_page }} of {{ invoicesMeta.last_page }}</div>
              </div>
              <div v-if="issuedTotals.length > 0" class="mb-3">
                <div v-for="t in issuedTotals" :key="t.currency" class="d-inline-flex align-items-center px-3 py-2 mr-2 rounded border border-warning bg-warning-light">
                  <i class="fa fa-exclamation-circle text-warning mr-2"></i>
                  <span class="small text-muted mr-2">Outstanding ({{ t.currency }})</span>
                  <strong>{{ t.amount.toFixed(2) }} {{ t.currency }}</strong>
                </div>
              </div>
              <div v-if="invoicesLoading" class="p-2"><i class="fa fa-spinner fa-spin mr-1"></i> Loading invoices...</div>
              <div v-else>
                <div v-if="invoicesError" class="alert alert-danger">{{ invoicesError }}</div>
                <div v-else>
                  <div v-if="(invoices || []).length === 0" class="text-muted">No invoices found.</div>
                  <div v-else class="table-responsive">
                    <table class="table table-sm table-striped">
                      <thead>
                        <tr>
                          <th>Number</th>
                          <th>Period</th>
                          <th>Status</th>
                          <th>Issue Date</th>
                          <th class="text-right">Total</th>
                          <th class="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="inv in invoices">
                          <tr :key="(inv.id || inv.number) + '-head'" @click="toggleInvoice(inv)" class="cursor-pointer">
                            <td>
                              <i class="fa mr-1" :class="isExpanded(inv) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                              {{ inv.number }}
                            </td>
                            <td>{{ inv.period }}</td>
                            <td><span class="badge" :class="statusClass(inv.status)">{{ statusText(inv.status) }}</span></td>
                            <td>{{ inv.issueDate }}</td>
                            <td class="text-right">{{ inv.total }} {{ inv.currency }}</td>
                            <td class="text-right" @click.stop>
                              <button class="btn btn-sm btn-outline-primary" :disabled="isDownloading(inv)" @click.stop="downloadInvoice(inv)" title="Download invoice PDF" data-testid="billing-invoice-download">
                                <i class="fa" :class="isDownloading(inv) ? 'fa-spinner fa-spin' : 'fa-download'"></i>
                              </button>
                              <button v-if="hasReceipt(inv)" class="btn btn-sm btn-outline-success ml-1" :disabled="isDownloading(inv, 'receipt')" @click.stop="downloadInvoice(inv, 'receipt')" title="Download payment receipt" data-testid="billing-invoice-receipt">
                                <i class="fa" :class="isDownloading(inv, 'receipt') ? 'fa-spinner fa-spin' : 'fa-download'"></i>
                                Receipt
                              </button>
                            </td>
                          </tr>
                          <tr v-if="isExpanded(inv)" :key="(inv.id || inv.number) + '-detail'">
                            <td colspan="6">
                              <div class="p-2 bg-light rounded">
                                <div v-if="getInvoiceLines(inv).length === 0" class="text-muted small">No lines available.</div>
                                <div v-else class="table-responsive">
                                  <table class="table table-sm mb-0">
                                    <thead>
                                      <tr>
                                        <th>Description</th>
                                        <th>Tier</th>
                                        <th class="text-right">Quantity * Price</th>
                                        <th v-if="hasLineTax(inv)" class="text-right">Tax rate</th>
                                        <th class="text-right">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="line in getInvoiceLines(inv)" :key="line.id || (line.description + String(line.unit_price))">
                                        <td>{{ line.description }}</td>
                                        <td>
                                          <span v-if="line.tier_label">{{ line.tier_label }}</span>
                                          <span v-else-if="line.tier_from == 1 && (line.tier_to == null || line.tier_to === '')"><i class="fa fa-minus text-muted" title="No tier"></i></span>
                                          <span v-else-if="line.tier_from || line.tier_to">{{ line.tier_from }}-{{ line.tier_to }}</span>
                                        </td>
                                        <td class="text-right">{{ line.quantity }} * {{ line.unit_price }}</td>
                                        <td v-if="hasLineTax(inv)" class="text-right" data-testid="billing-invoice-line-tax-rate">{{ taxRate(line.tax_rate) }}</td>
                                        <td class="text-right">{{ line.amount }} {{ inv.currency }}</td>
                                      </tr>
                                    </tbody>
                                    <tfoot v-if="inv.tax !== null && inv.tax !== undefined" data-testid="billing-invoice-totals">
                                      <tr>
                                        <td :colspan="hasLineTax(inv) ? 4 : 3" class="text-right">Subtotal</td>
                                        <td class="text-right">{{ inv.subtotal }} {{ inv.currency }}</td>
                                      </tr>
                                      <tr>
                                        <td :colspan="hasLineTax(inv) ? 4 : 3" class="text-right">Tax</td>
                                        <td class="text-right" data-testid="billing-invoice-tax">{{ inv.tax }} {{ inv.currency }}</td>
                                      </tr>
                                      <tr>
                                        <th :colspan="hasLineTax(inv) ? 4 : 3" class="text-right">Total</th>
                                        <th class="text-right">{{ inv.total }} {{ inv.currency }}</th>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </table>
                    <div class="d-flex align-items-center justify-content-between">
                      <button class="btn btn-sm btn-secondary" :disabled="(invoicesMeta.current_page || 1) <= 1" @click="onInvoicesPrev">
                        <i class="fa fa-chevron-left mr-1"></i> Prev
                      </button>
                      <div class="small text-muted">Showing {{ invoices.length }} of {{ invoicesMeta.total }}</div>
                      <button class="btn btn-sm btn-secondary" :disabled="(invoicesMeta.current_page || 1) >= (invoicesMeta.last_page || 1)" @click="onInvoicesNext">
                        Next <i class="fa fa-chevron-right ml-1"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </page-content>
  </div>
</template>

<script>
import BillingService from '@/services/billing.service'
import { CARD_PAYMENTS_ENABLED } from '@/config/billing'

export default {
  data () {
    return {
      profiles: [],
      loading: false,
      error: null,
      // Invoices
      invoices: [],
      issuedTotals: [],
      invoicesLoading: false,
      invoicesError: null,
      invoicesMeta: { current_page: 1, last_page: 1, per_page: 15, total: 0 },
      // Track which invoices are expanded (by id/number-derived key)
      expandedInvoices: {},
      // Track per-invoice downloading state
      downloadingInvoices: {},
      // saved card is hidden while card payments are off (config/billing.js)
      cardPaymentsEnabled: CARD_PAYMENTS_ENABLED
    }
  },
  mounted () {
    this.fetchProfiles()
    this.fetchInvoices(1)
  },
  methods: {
    fetchProfiles () {
      this.loading = true
      this.error = null
      BillingService.index()
        .then(res => {
          // Accept either array response or wrapped data
          const data = Array.isArray(res.data) ? res.data : (res.data?.data || res.data || [])
          this.profiles = data || []
        })
        .catch(err => {
          this.error = err?.response?.data?.message || err?.message || 'Failed to load billing profiles.'
        })
        .finally(() => {
          this.loading = false
        })
    },
    hasAddress (p) {
      return p.addressLine1 || p.addressLine2 || p.city || p.state || p.postalCode
    },
    formatAddress (p) {
      const line1 = [p.addressLine1, p.addressLine2].filter(Boolean).join(' ')
      const line2 = [p.city, p.state, p.postalCode].filter(Boolean).join(', ')
      return [line1, line2].filter(Boolean).join(' | ')
    },
    formatPayment (p) {
      if (p.pmType && p.pmLastFour) return `${p.pmType.toUpperCase()} •••• ${p.pmLastFour}`
      if (p.pmType) return p.pmType.toUpperCase()
      if (p.pmLastFour) return `•••• ${p.pmLastFour}`
      return ''
    },
    fetchInvoices (page = 1) {
      this.invoicesLoading = true
      this.invoicesError = null
      const params = { page }
      BillingService.invoices(params)
        .then(res => {
          const payload = (res && res.data && Array.isArray(res.data.data)) ? res.data : (res && res.data ? res.data : {})
          const items = payload && payload.data ? payload.data : []
          this.invoices = items
          this.issuedTotals = Array.isArray(payload.issued_totals) ? payload.issued_totals : []
          this.invoicesMeta = {
            current_page: payload.current_page || 1,
            last_page: payload.last_page || 1,
            per_page: payload.per_page || (payload.meta && payload.meta.per_page) || this.invoicesMeta.per_page,
            total: payload.total || (payload.meta && payload.meta.total) || items.length
          }
        })
        .catch(err => {
          this.invoicesError = err && err.response && err.response.data && err.response.data.message ? err.response.data.message : (err && err.message ? err.message : 'Failed to load invoices.')
        })
        .finally(() => {
          this.invoicesLoading = false
        })
    },
    onInvoicesPrev () {
      const p = this.invoicesMeta && this.invoicesMeta.current_page ? this.invoicesMeta.current_page : 1
      if (p > 1) this.fetchInvoices(p - 1)
    },
    onInvoicesNext () {
      const p = this.invoicesMeta && this.invoicesMeta.current_page ? this.invoicesMeta.current_page : 1
      const last = this.invoicesMeta && this.invoicesMeta.last_page ? this.invoicesMeta.last_page : 1
      if (p < last) this.fetchInvoices(p + 1)
    },
    statusText (status) {
      const s = String(status || '').toUpperCase()
      switch (s) {
        case 'PAID':
          return 'PAID'
        case 'PARTIALLY_PAID':
          return 'PARTIALLY PAID'
        case 'ISSUED':
          return 'NOT PAID'
        case 'VOID':
          return 'VOID'
        default:
          return 'NONE'
      }
    },
    statusClass (status) {
      const s = String(status || '').toUpperCase()
      switch (s) {
        case 'PAID':
          return 'badge-success'
        case 'PARTIALLY_PAID':
          return 'badge-info'
        case 'ISSUED':
          return 'badge-warning'
        case 'VOID':
          return 'badge-light'
        default:
          return 'badge-light'
      }
    },
    invoiceKey (inv) {
      // Prefer numeric/string id, fallback to number, otherwise an index-like string
      if (inv && inv.id != null) return String(inv.id)
      if (inv && inv.number != null) return String(inv.number)
      try { return JSON.stringify(inv) } catch (e) { return String(Math.random()).slice(2) }
    },
    isExpanded (inv) {
      const k = this.invoiceKey(inv)
      return !!this.expandedInvoices[k]
    },
    toggleInvoice (inv) {
      const k = this.invoiceKey(inv)
      const cur = !!this.expandedInvoices[k]
      // Ensure reactivity when adding new keys to an object
      this.$set(this.expandedInvoices, k, !cur)
    },
    // Paddle's tax rate per line, the way its invoice shows it (credits have none, they went to Paddle as a discount)
    hasLineTax (inv) {
      return this.getInvoiceLines(inv).some(line => line.tax_rate !== null && line.tax_rate !== undefined)
    },
    taxRate (rate) {
      if (rate === null || rate === undefined || rate === '') return '-'
      return `${parseFloat((Number(rate) * 100).toFixed(2))}%`
    },
    getInvoiceLines (inv) {
      const candidates = [inv && inv.lines, inv && inv.line_items, inv && inv.items, inv && inv.invoice_lines]
      const lines = candidates.find(arr => Array.isArray(arr)) || []
      if (Array.isArray(lines)) return lines
      if (lines && typeof lines === 'object') return Object.values(lines)
      return []
    },
    // Core keeps the receipt of a paid Stripe/Paddle invoice
    hasReceipt (inv) {
      return Array.isArray(inv && inv.documentTypes) && inv.documentTypes.includes('receipt')
    },
    isDownloading (inv, kind = 'invoice') {
      const k = `${this.invoiceKey(inv)}:${kind}`
      return !!this.downloadingInvoices[k]
    },
    async downloadInvoice (inv, kind = 'invoice') {
      const k = `${this.invoiceKey(inv)}:${kind}`
      const invoice = (inv && (inv.id != null ? inv.id : inv.number != null ? inv.number : ''))
      if (!invoice && invoice !== 0) {
        this.invoicesError = 'Invalid invoice identifier.'
        return
      }
      this.$set(this.downloadingInvoices, k, true)
      const parseFilename = (disposition, fallback) => {
        try {
          if (!disposition) return fallback
          // Try filename* (RFC 5987)
          const starMatch = disposition.match(/filename\*=(?:UTF-8''|)([^;]+)/i)
          if (starMatch && starMatch[1]) {
            const v = decodeURIComponent(starMatch[1].replace(/"/g, '').trim())
            return v || fallback
          }
          const match = disposition.match(/filename="?([^";]+)"?/i)
          if (match && match[1]) return match[1]
        } catch (e) {}
        return fallback
      }
      try {
        const res = kind === 'receipt' ? await BillingService.downloadReceipt(invoice) : await BillingService.download(invoice)
        const contentType = (res && res.headers && res.headers['content-type']) ? res.headers['content-type'] : 'application/pdf'
        if ((contentType || '').includes('application/json')) {
          try {
            const txt = await res.data.text()
            const j = JSON.parse(txt)
            this.invoicesError = j && j.message ? j.message : 'Unable to download invoice'
          } catch (e) {
            this.invoicesError = 'Unable to download invoice'
          }
          return
        }
        const disposition = res && res.headers ? res.headers['content-disposition'] : ''
        const fallbackName = `${kind === 'receipt' ? 'receipt' : 'invoice'}${inv && inv.number ? `-${inv.number}` : ''}.pdf`
        const filename = parseFilename(disposition, fallbackName)
        const blob = new Blob([res.data], { type: contentType })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        const msg = err && err.response && err.response.data && err.response.data.message
          ? err.response.data.message
          : (err && err.message ? err.message : 'Failed to download invoice')
        this.invoicesError = msg
      } finally {
        this.$set(this.downloadingInvoices, k, false)
      }
    }
  }
}
</script>
